/**
 * Audit Trail Viewer Component
 *
 * Admin-only component for viewing audit logs and change history.
 * Provides filtering, statistics, and detailed change tracking.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Card,
  Grid,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tooltip,
  Collapse,
} from '@mui/material';
import {
  Visibility,
  FilterList,
  Refresh,
  History,
  Person,
  DateRange,
  ExpandMore,
  ExpandLess,
  TrendingUp,
} from '@mui/icons-material';
import { useApp } from '../../contexts/AuthContext';
import { auditService, AuditLogEntry, AuditLogDetail, AuditStatistics } from '../../services';

// Action type colors
const getActionColor = (action: string) => {
  switch (action.toLowerCase()) {
    case 'create': return '#10b981';
    case 'update': return '#3b82f6';
    case 'delete': return '#ef4444';
    case 'approve': return '#8b5cf6';
    case 'reject': return '#f59e0b';
    case 'login': return '#06b6d4';
    case 'logout': return '#6b7280';
    default: return '#9ca3af';
  }
};

// Entity type labels
const getEntityLabel = (entityType: string) => {
  const labels: Record<string, string> = {
    emissions: 'Emissions',
    water: 'Water',
    waste: 'Waste',
    renewable: 'Renewable Energy',
    social: 'Social',
    facility: 'Facility',
    user: 'User',
    organization: 'Organization',
  };
  return labels[entityType] || entityType;
};

interface FilterState {
  action: string;
  entity_type: string;
  user_email: string;
  start_date: string;
  end_date: string;
}

export const AuditTrailViewer: React.FC = () => {
  const { user } = useApp();

  // State
  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Filter state
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<FilterState>({
    action: '',
    entity_type: '',
    user_email: '',
    start_date: '',
    end_date: '',
  });

  // Detail dialog
  const [selectedLog, setSelectedLog] = useState<AuditLogDetail | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Statistics
  const [stats, setStats] = useState<AuditStatistics | null>(null);
  const [showStats, setShowStats] = useState(true);

  // Fetch logs
  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await auditService.listLogs({
        action: filters.action || undefined,
        entity_type: filters.entity_type || undefined,
        user_email: filters.user_email || undefined,
        start_date: filters.start_date || undefined,
        end_date: filters.end_date || undefined,
        limit: rowsPerPage,
        offset: page * rowsPerPage,
      });
      setLogs(response.logs);
      setTotal(response.total);
    } catch (err: any) {
      console.error('Failed to fetch audit logs:', err);
      setError('Failed to load audit logs. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [filters, page, rowsPerPage]);

  // Fetch statistics
  const fetchStats = useCallback(async () => {
    try {
      const response = await auditService.getStatistics(30);
      setStats(response);
    } catch (err: any) {
      console.error('Failed to fetch audit statistics:', err);
    }
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // Handle page change
  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle filter change
  const handleFilterChange = (field: keyof FilterState, value: string) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(0);
  };

  // Clear filters
  const handleClearFilters = () => {
    setFilters({
      action: '',
      entity_type: '',
      user_email: '',
      start_date: '',
      end_date: '',
    });
    setPage(0);
  };

  // View log detail
  const handleViewDetail = async (log: AuditLogEntry) => {
    setLoadingDetail(true);
    setDetailDialogOpen(true);
    try {
      const detail = await auditService.getLog(log.id);
      setSelectedLog(detail);
    } catch (err) {
      console.error('Failed to fetch log detail:', err);
      setSelectedLog(log as AuditLogDetail);
    } finally {
      setLoadingDetail(false);
    }
  };

  // Format timestamp
  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <History sx={{ fontSize: 32, color: '#6366f1' }} />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              Audit Trail
            </Typography>
            <Typography variant="body2" color="text.secondary">
              View and track all system changes and user activities
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Toggle Statistics">
            <IconButton onClick={() => setShowStats(!showStats)}>
              <TrendingUp />
            </IconButton>
          </Tooltip>
          <Tooltip title="Toggle Filters">
            <IconButton onClick={() => setShowFilters(!showFilters)}>
              <FilterList />
            </IconButton>
          </Tooltip>
          <Tooltip title="Refresh">
            <IconButton onClick={fetchLogs}>
              <Refresh />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Statistics Cards */}
      <Collapse in={showStats}>
        {stats && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ border: '1px solid #e5e7eb', boxShadow: 'none' }}>
                <Box sx={{ p: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Total Actions (30 days)
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 600, color: '#6366f1' }}>
                    {stats.total_actions.toLocaleString()}
                  </Typography>
                </Box>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ border: '1px solid #e5e7eb', boxShadow: 'none' }}>
                <Box sx={{ p: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Active Users
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 600, color: '#10b981' }}>
                    {stats.most_active_users.length}
                  </Typography>
                </Box>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ border: '1px solid #e5e7eb', boxShadow: 'none' }}>
                <Box sx={{ p: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Entity Types
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 600, color: '#3b82f6' }}>
                    {Object.keys(stats.actions_by_entity).length}
                  </Typography>
                </Box>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ border: '1px solid #e5e7eb', boxShadow: 'none' }}>
                <Box sx={{ p: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Action Types
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
                    {Object.entries(stats.actions_by_type).slice(0, 4).map(([action, count]) => (
                      <Chip
                        key={action}
                        label={`${action}: ${count}`}
                        size="small"
                        sx={{
                          backgroundColor: getActionColor(action),
                          color: '#fff',
                          fontSize: '0.7rem',
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              </Card>
            </Grid>
          </Grid>
        )}
      </Collapse>

      {/* Filters */}
      <Collapse in={showFilters}>
        <Paper sx={{ p: 2, mb: 2, border: '1px solid #e5e7eb', boxShadow: 'none' }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Action</InputLabel>
                <Select
                  value={filters.action}
                  label="Action"
                  onChange={(e) => handleFilterChange('action', e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="create">Create</MenuItem>
                  <MenuItem value="update">Update</MenuItem>
                  <MenuItem value="delete">Delete</MenuItem>
                  <MenuItem value="approve">Approve</MenuItem>
                  <MenuItem value="reject">Reject</MenuItem>
                  <MenuItem value="login">Login</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Entity Type</InputLabel>
                <Select
                  value={filters.entity_type}
                  label="Entity Type"
                  onChange={(e) => handleFilterChange('entity_type', e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="emissions">Emissions</MenuItem>
                  <MenuItem value="water">Water</MenuItem>
                  <MenuItem value="waste">Waste</MenuItem>
                  <MenuItem value="renewable">Renewable</MenuItem>
                  <MenuItem value="social">Social</MenuItem>
                  <MenuItem value="facility">Facility</MenuItem>
                  <MenuItem value="user">User</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                label="User Email"
                value={filters.user_email}
                onChange={(e) => handleFilterChange('user_email', e.target.value)}
                placeholder="Search by email"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Start Date"
                type="date"
                value={filters.start_date}
                onChange={(e) => handleFilterChange('start_date', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                label="End Date"
                type="date"
                value={filters.end_date}
                onChange={(e) => handleFilterChange('end_date', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Button variant="outlined" onClick={handleClearFilters} fullWidth>
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Collapse>

      {/* Audit Logs Table */}
      <Paper sx={{ border: '1px solid #e5e7eb', boxShadow: 'none' }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f9fafb' }}>
                <TableCell sx={{ fontWeight: 600, py: 1.5 }}>Timestamp</TableCell>
                <TableCell sx={{ fontWeight: 600, py: 1.5 }}>User</TableCell>
                <TableCell sx={{ fontWeight: 600, py: 1.5 }}>Action</TableCell>
                <TableCell sx={{ fontWeight: 600, py: 1.5 }}>Entity Type</TableCell>
                <TableCell sx={{ fontWeight: 600, py: 1.5 }}>Description</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600, py: 1.5, width: 80 }}>Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4, color: '#9ca3af' }}>
                    No audit logs found
                  </TableCell>
                </TableRow>
              ) : (
                logs.map((log) => (
                  <TableRow key={log.id} hover>
                    <TableCell sx={{ fontSize: '0.8rem' }}>
                      {formatTimestamp(log.timestamp)}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Person sx={{ fontSize: 16, color: '#9ca3af' }} />
                        <Box>
                          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                            {log.user_name || log.user_email}
                          </Typography>
                          {log.user_role && (
                            <Typography variant="caption" color="text.secondary">
                              {log.user_role}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={log.action}
                        size="small"
                        sx={{
                          backgroundColor: getActionColor(log.action),
                          color: '#fff',
                          fontWeight: 500,
                          fontSize: '0.7rem',
                          textTransform: 'capitalize',
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8rem' }}>
                      {getEntityLabel(log.entity_type)}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8rem', maxWidth: 300 }}>
                      <Typography noWrap title={log.description || ''}>
                        {log.description || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="View Details">
                        <IconButton size="small" onClick={() => handleViewDetail(log)}>
                          <Visibility sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>

      {/* Detail Dialog */}
      <Dialog open={detailDialogOpen} onClose={() => setDetailDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Audit Log Details
        </DialogTitle>
        <DialogContent dividers>
          {loadingDetail ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : selectedLog ? (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Timestamp</Typography>
                <Typography variant="body1">{formatTimestamp(selectedLog.timestamp)}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">User</Typography>
                <Typography variant="body1">
                  {selectedLog.user_name || selectedLog.user_email}
                  {selectedLog.user_role && ` (${selectedLog.user_role})`}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Action</Typography>
                <Chip
                  label={selectedLog.action}
                  size="small"
                  sx={{
                    backgroundColor: getActionColor(selectedLog.action),
                    color: '#fff',
                    textTransform: 'capitalize',
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Entity Type</Typography>
                <Typography variant="body1">{getEntityLabel(selectedLog.entity_type)}</Typography>
              </Grid>
              {selectedLog.entity_id && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Entity ID</Typography>
                  <Typography variant="body1" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                    {selectedLog.entity_id}
                  </Typography>
                </Grid>
              )}
              {selectedLog.description && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Description</Typography>
                  <Typography variant="body1">{selectedLog.description}</Typography>
                </Grid>
              )}
              {selectedLog.ip_address && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">IP Address</Typography>
                  <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                    {selectedLog.ip_address}
                  </Typography>
                </Grid>
              )}
              {selectedLog.endpoint && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Endpoint</Typography>
                  <Typography variant="body1" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                    {selectedLog.http_method} {selectedLog.endpoint}
                  </Typography>
                </Grid>
              )}
              {selectedLog.previous_values && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Previous Values</Typography>
                  <Paper sx={{ p: 1.5, backgroundColor: '#fef2f2', mt: 0.5 }}>
                    <pre style={{ margin: 0, fontSize: '0.75rem', overflow: 'auto' }}>
                      {JSON.stringify(selectedLog.previous_values, null, 2)}
                    </pre>
                  </Paper>
                </Grid>
              )}
              {selectedLog.new_values && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">New Values</Typography>
                  <Paper sx={{ p: 1.5, backgroundColor: '#f0fdf4', mt: 0.5 }}>
                    <pre style={{ margin: 0, fontSize: '0.75rem', overflow: 'auto' }}>
                      {JSON.stringify(selectedLog.new_values, null, 2)}
                    </pre>
                  </Paper>
                </Grid>
              )}
              {selectedLog.changes && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Changes</Typography>
                  <Paper sx={{ p: 1.5, backgroundColor: '#f9fafb', mt: 0.5 }}>
                    <pre style={{ margin: 0, fontSize: '0.75rem', overflow: 'auto' }}>
                      {JSON.stringify(selectedLog.changes, null, 2)}
                    </pre>
                  </Paper>
                </Grid>
              )}
            </Grid>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AuditTrailViewer;
